/**
 * Pure helpers for SettingsView state merges (CC ≤ 4).
 */

import type {
  AiGenerationSettings,
  AiTaskId,
  JiraConnectionSettings,
  SlackVoiceSettings,
  UserSettingsPublic,
  UserSettingsUpdate,
} from '../../services/clients/types';
import { daysUntil } from '../../domain/sourceConnection';

export function configuredLabel(configured: boolean, lastFour: string | null): string {
  if (!configured) return 'Sin configurar';
  return lastFour ? `Conectado · ••••${lastFour}` : 'Conectado';
}

/** Chip copy for Jira connection acknowledge (Verificado = probe-only). */
export function jiraAckChipLabel(
  ack: import('@zunelo/shared').JiraConnectionAck | null,
  fallbackConnected: boolean,
): string {
  if (!ack) {
    return fallbackConnected ? 'Conectado' : 'No configurado';
  }
  if (ack.status === 'mock') return 'Mock · datos locales';
  if (ack.status === 'error') return 'Error de conexión';
  if (ack.status === 'not_configured') return 'No configurado';
  if (ack.displayName || ack.accountEmail) {
    return `Verificado · ${ack.displayName || ack.accountEmail}`;
  }
  if (ack.method === 'oauth') {
    return ack.siteName ? `OAuth · ${ack.siteName}` : 'OAuth conectado';
  }
  if (ack.method === 'mock') return 'Mock · datos locales';
  return 'Credenciales guardadas';
}

/** Success-styled chip only for mock or probe-verified identity. */
export function jiraAckChipOk(
  ack: import('@zunelo/shared').JiraConnectionAck | null,
  fallbackConnected: boolean,
): boolean {
  if (ack?.status === 'mock') return true;
  if (ack?.status === 'ok' && (ack.displayName || ack.accountEmail)) return true;
  if (ack?.status === 'ok') return false;
  if (ack?.status === 'error' || ack?.status === 'not_configured') return false;
  return fallbackConnected;
}

export interface ConnectionAckBanner {
  title: string;
  detail: string;
}

/** Shared Settings banner when a GitHub PAT is stored (not yet /user-verified). */
export function githubConnectionBanner(
  configured: boolean,
  lastFour: string | null,
  ack: import('@zunelo/shared').GitHubConnectionAck | null = null,
): ConnectionAckBanner | null {
  if (ack?.status === 'mock') {
    return {
      title: '✓ Conexión lista · mock',
      detail: 'El workspace de GitHub usa datos locales. No se llama a la API de GitHub.',
    };
  }
  if (ack?.status === 'error' || ack?.status === 'not_configured') {
    return null;
  }
  if (ack?.status === 'ok' && ack.login) {
    return {
      title: `✓ Conexión verificada · ${ack.login}`,
      detail: 'GitHub respondió a /user. El workspace puede listar tus pull requests.',
    };
  }
  if (!configured) return null;
  return {
    title: lastFour
      ? `✓ Credenciales guardadas · token ••••${lastFour}`
      : '✓ Credenciales guardadas',
    detail:
      'Usa Probar conexión para verificar el token con GitHub. Guardar no prueba que el PAT sea válido.',
  };
}

export function githubAckChipLabel(
  ack: import('@zunelo/shared').GitHubConnectionAck | null,
  configured: boolean,
  lastFour: string | null,
): string {
  if (ack?.status === 'mock') return 'Mock · datos locales';
  if (ack?.status === 'error') return 'Error de conexión';
  if (ack?.status === 'not_configured') return 'No configurado';
  if (ack?.status === 'ok' && ack.login) return `Verificado · ${ack.login}`;
  return configuredLabel(configured, lastFour);
}

export function githubAckChipOk(
  ack: import('@zunelo/shared').GitHubConnectionAck | null,
  configured: boolean,
): boolean {
  if (ack?.status === 'mock') return true;
  if (ack?.status === 'ok' && ack.login) return true;
  if (ack?.status === 'error' || ack?.status === 'not_configured') return false;
  return configured;
}

/**
 * Shared Settings banner for Jira: verified identity after probe, mock, or
 * credentials-ready (prompting an explicit test). Never success-styled on error.
 */
export function jiraConnectionBanner(
  ack: import('@zunelo/shared').JiraConnectionAck | null,
  credentialsReady: boolean,
): ConnectionAckBanner | null {
  if (ack?.status === 'error' || ack?.status === 'not_configured') {
    return null;
  }
  if (ack?.status === 'mock') {
    return {
      title: '✓ Conexión lista · mock',
      detail: 'El workspace de Jira usa datos locales. No se llama a Atlassian.',
    };
  }
  if (ack?.status === 'ok' && (ack.displayName || ack.accountEmail)) {
    const who = ack.displayName || ack.accountEmail || '';
    return {
      title: `✓ Conexión verificada · ${who}`,
      detail: 'Atlassian respondió a /myself. El workspace de Jira puede usar esta cuenta.',
    };
  }
  if (!credentialsReady) return null;
  return {
    title: '✓ Credenciales listas',
    detail: 'Usa Probar conexión para verificar la identidad en Atlassian.',
  };
}

export function defaultAiDraft(): AiGenerationSettings {
  return { provider: 'opencode-go', model: 'deepseek-v4-flash' };
}

export function defaultVoiceDraft(): SlackVoiceSettings {
  return {
    language: 'es',
    tone: 'professional',
    replyLength: 'medium',
    customPrompt: '',
  };
}

export function voiceSelectsEqual(a: SlackVoiceSettings, b: SlackVoiceSettings): boolean {
  return a.language === b.language && a.tone === b.tone && a.replyLength === b.replyLength;
}

export function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: unknown }).message);
  }
  return fallback;
}

export function nextVoiceDraft(
  data: UserSettingsPublic,
  currentPrompt: string,
  savedPrompt: string,
  forceNotes?: boolean,
): { voice: SlackVoiceSettings; keepNotes: boolean } {
  const dirty = currentPrompt.trim() !== savedPrompt.trim();
  const keepNotes = dirty && !forceNotes;
  return {
    keepNotes,
    voice: {
      ...data.slackVoice,
      customPrompt: keepNotes ? currentPrompt : data.slackVoice.customPrompt,
    },
  };
}

export function defaultJiraDraft(): JiraConnectionSettings {
  return {
    baseUrl: '',
    email: '',
    authMethod: 'none',
    oauthConnected: false,
    oauthSiteName: '',
  };
}

export function jiraConnectionEqual(a: JiraConnectionSettings, b: JiraConnectionSettings): boolean {
  return (
    a.baseUrl.trim() === b.baseUrl.trim() &&
    a.email.trim() === b.email.trim() &&
    a.authMethod === b.authMethod
  );
}

/**
 * Build the `ai` patch actually sent over the wire.
 *
 * The server merges `tasks` key by key, so a task the draft dropped has to be
 * sent as an explicit `{}` — omitting it means "leave it alone", which is why
 * "Restaurar valores globales" silently did nothing. Same reason `effort` is
 * sent as `null` rather than omitted when the user clears it.
 */
export function aiSavePatch(
  next: AiGenerationSettings,
  current: AiGenerationSettings,
): NonNullable<UserSettingsUpdate['ai']> {
  const tasks: NonNullable<AiGenerationSettings['tasks']> = { ...(next.tasks ?? {}) };
  for (const task of Object.keys(current.tasks ?? {}) as AiTaskId[]) {
    if (!tasks[task]) tasks[task] = {};
  }
  return {
    provider: next.provider,
    model: next.model,
    effort: next.effort ?? null,
    tasks,
  };
}

/**
 * True when the draft route differs from what the server holds. Per-task
 * overrides are compared structurally: a task's provider/model/effort/tone/
 * prompt all reach the server through the same auto-save.
 */
export function aiChanged(next: AiGenerationSettings, current: AiGenerationSettings): boolean {
  if (next.provider !== current.provider || next.model !== current.model) return true;
  if ((next.effort ?? '') !== (current.effort ?? '')) return true;
  return JSON.stringify(next.tasks ?? {}) !== JSON.stringify(current.tasks ?? {});
}

export function shouldForceNotesOnPatch(
  forceNotes: boolean | undefined,
  patchHasCustomPrompt: boolean,
): boolean {
  if (forceNotes !== undefined) return forceNotes;
  return patchHasCustomPrompt;
}

export type ProviderHeaderTone = 'ok' | 'warn' | 'danger' | 'muted';

export interface ProviderHeaderStatus {
  label: string;
  ok: boolean;
  tone: ProviderHeaderTone;
}

/** Warn this many days before a known credential expiry. */
export const CREDENTIAL_EXPIRY_WARNING_DAYS = 7;

/**
 * Header chip for a source provider: a rejected credential or a known expiry
 * overrides the plain configured / not-configured wording.
 */
export function providerHeaderStatus(
  configured: boolean,
  summary:
    | Pick<import('@zunelo/shared').SourceProviderSummary, 'credentialStatus' | 'expiresAt'>
    | null
    | undefined,
  now: number = Date.now(),
): ProviderHeaderStatus {
  if (!configured) return { label: 'No configurado', ok: false, tone: 'muted' };
  if (summary?.credentialStatus === 'rejected') {
    return { label: 'Credencial rechazada', ok: false, tone: 'danger' };
  }
  const days = daysUntil(summary?.expiresAt, now);
  if (days !== null && days <= 0) return { label: 'Token vencido', ok: false, tone: 'danger' };
  if (days !== null && days <= CREDENTIAL_EXPIRY_WARNING_DAYS) {
    return {
      label: days === 1 ? 'Vence mañana' : `Vence en ${days} días`,
      ok: true,
      tone: 'warn',
    };
  }
  return { label: 'Configurado', ok: true, tone: 'ok' };
}

/** ISO timestamp → `yyyy-mm-dd` for a date input ('' when unknown). */
export function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toISOString().slice(0, 10);
}

/** `yyyy-mm-dd` from a date input → end-of-day UTC ISO, or null when cleared. */
export function fromDateInputValue(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const ms = Date.parse(`${value}T23:59:59.000Z`);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}
